// hooks/useFolders.js
import { useState, useCallback } from "react";
import TokenChecker, { API_BASE_URL } from "../components/TokenChecker"; // adjust path if needed
import { useAuth } from "../Contexts/AuthContext";
import { log, warn, error as logError } from "../utils/logger";

// If your API isn’t prefixed, set to "" and keep trailing slashes below.
const API_PREFIX = "/api";



const jsonOrThrow = async (res) => {
  let data = null;
  try { data = await res.json(); } catch (_) {}
  if (!res.ok) {
    const msg = (data && (data.detail || data.error)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
};

const useFolder = () => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);

  const {authData} = useAuth();

  const fetchFolders = useCallback(async (includeArchived = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_PREFIX}/folders/?archived=${includeArchived ? "1" : "0"}`;
      const res = await TokenChecker(url, { method: "GET" });
      const data = await jsonOrThrow(res);
     
      // Your list view returns { folders: [...] }
      setFolders(Array.isArray(data?.folders) ? data.folders : []);
      return data;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);



async function uploadToS3(file, token) {
  log("🔍 Debug uploadToS3:");
  log("File:", file?.name, file?.size, file?.type);
  // Never log full tokens in production
  warn("Token present:", Boolean(token));
  
  if (!token) {
    throw new Error("No authentication token provided");
  }
  
  if (!file) {
    throw new Error("No file provided");
  }

  const presign = await fetch(`${API_BASE_URL}/api/uploads/presign/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type || "application/octet-stream",
    }),
  });

  log("Presign response status:", presign.status);
  log("Presign response headers:", Object.fromEntries(presign.headers.entries()));

  if (!presign.ok) {
    const errorText = await presign.text();
    logError("Presign error response:", errorText);
    throw new Error(`Failed to presign: ${presign.status} ${presign.statusText}`);
  }

  const { url, key, contentType, headers: requiredHeaders } = await presign.json();
  const mask = (s) => (typeof s === 'string' && s.length > 8 ? `${s.slice(0,4)}…${s.slice(-4)}` : s);
  log("✅ Got presigned URL, key:", mask(key));

  // Use required headers from backend (includes Content-Type and optional SSE)
  const putHeaders = requiredHeaders && typeof requiredHeaders === 'object'
    ? requiredHeaders
    : { "Content-Type": contentType };

  const uploadRes = await fetch(url, {
    method: "PUT",
    headers: putHeaders,
    body: file,
  })

  if (!uploadRes.ok) {
    try {
      const errText = await uploadRes.text();
      warn("S3 upload failed:", uploadRes.status, errText);
    } catch(_) {}
    throw new Error("Upload failed");
  }

  log("✅ File uploaded to S3 with key:", mask(key));
  return { key }; // store only S3 key; resolve URL server-side when needed
}




  const createFolder = useCallback(async (name, color = "#4299E1", baseFile, baseFileLabel, extra = {}) => {
    setLoading(true);
    setError(null);
    try {
      
      let baseFileKey = null;
      let baseFileUrl = null;
      if (baseFile) {
        const {key} = await uploadToS3(baseFile, authData.accessToken);
        baseFileKey = key;
        const bucket = process.env.REACT_APP_S3_BUCKET || process.env.REACT_APP_AWS_S3_BUCKET;
        const region = process.env.REACT_APP_S3_REGION || process.env.REACT_APP_AWS_DEFAULT_REGION || 'us-east-1';
        const publicUrl = bucket ? `https://${bucket}.s3.${region}.amazonaws.com/${encodeURIComponent(key)}` : '';
        baseFileUrl = publicUrl;
      }

      const payload = {
        name,
        color,
        baseFileKey: baseFileKey || null, // optional attachment
        baseFileLabel: baseFileLabel || "", // optional label
        baseFileUrl: baseFileUrl || "", // optional URL
        // Serializer accepts these; safe defaults:
        sort_order: extra.sort_order ?? 0,
        is_archived: extra.is_archived ?? false,
      };



      const res = await TokenChecker(`${API_PREFIX}/folders/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const created = await jsonOrThrow(res);
      setFolders((prev) => [...prev, created]);
      return created;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFolder = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      // Build payload selectively. Supports:
      // - name, color, sort_order, is_archived
      // - baseFile (File) to replace
      // - baseFileLabel (string) to set/update label
      // - removeBaseFile (boolean) to remove existing file
      const payload = {};

      if (typeof data?.name === 'string') payload.name = data.name;
      if (typeof data?.color === 'string') payload.color = data.color;
      if (typeof data?.sort_order === 'number') payload.sort_order = data.sort_order;
      if (typeof data?.is_archived === 'boolean') payload.is_archived = data.is_archived;

      // Handle removal request explicitly
      if (data?.removeBaseFile === true) {
        payload.baseFileKey = null;
        payload.baseFileUrl = null;
        payload.baseFileLabel = "";
      }

      // Handle replacement upload
      if (data?.baseFile instanceof File) {
        const { key } = await uploadToS3(data.baseFile, authData.accessToken);
        const bucket = process.env.REACT_APP_S3_BUCKET || process.env.REACT_APP_AWS_S3_BUCKET;
        const region = process.env.REACT_APP_S3_REGION || process.env.REACT_APP_AWS_DEFAULT_REGION || 'us-east-1';
        const publicUrl = bucket ? `https://${bucket}.s3.${region}.amazonaws.com/${encodeURIComponent(key)}` : '';
        payload.baseFileKey = key;
        payload.baseFileUrl = publicUrl;
        payload.baseFileLabel = data?.baseFileLabel || data.baseFile.name || "";
      } else if (typeof data?.baseFileLabel === 'string' && !('baseFileKey' in payload)) {
        // Label-only update (no new file, no removal)
        payload.baseFileLabel = data.baseFileLabel;
      }

      const res = await TokenChecker(`${API_PREFIX}/folders/${id}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const updated = await jsonOrThrow(res);

      // Your PUT view likely returns the updated folder; if not, re-fetch.
      setFolders((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      return updated;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteFolder = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await TokenChecker(`${API_PREFIX}/folders/${id}/`, {
        method: "DELETE",
      });
      // Some DELETEs return 204 (no content)
      if (!res.ok && res.status !== 204) await jsonOrThrow(res);
      setFolders((prev) => prev.filter((f) => f.id !== id));
      return true;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const attachTasksToFolder = useCallback(async (folderId, taskIds) => {
  setLoading(true);
  setError(null);
  try {
    const res = await TokenChecker(`${API_PREFIX}/folders/${folderId}/attach-tasks/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ task_ids: taskIds }),
    });
    const data = await jsonOrThrow(res); // { updated_count, updated_task_ids, folder }
    // keep folders fresh (optional)
    setFolders((prev) => prev.map((f) => (f.id === data.folder.id ? data.folder : f)));
    return data;
  } catch (e) {
    setError(e);
    throw e;
  } finally {
    setLoading(false);
  }
}, []);

  return {
    folders,
    fetchFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    loading,
    error,
    setFolders, // expose in case you want custom local tweaks
    attachTasksToFolder, // for task management
  };
};

export default useFolder;
